// @flow

import { LOCATION_CHANGE } from "connected-react-router";
import { startRequest, finishRequest } from "@store/utils";

import {
  GetComplianceTrendsDefaultReports,
  GetComplianceTrendsDefaultReportsSuccess,
  GetComplianceTrendsDefaultReportsError,
  GetComplianceTrendsMyReports,
  GetComplianceTrendsMyReportsSuccess,
  GetComplianceTrendsMyReportsError,
  GetComplianceTrendsReportData,
  GetComplianceTrendsReportDataSuccess,
  GetComplianceTrendsReportDataError,
  GetComplianceTrendsReportSchemaSuccess,
  GetComplianceTrendsReportSchemaError,
  SetComplianceTrendsReportToSave,
  ReSetComplianceTrendsReportToSave,
  SetComplianceReportViewState,
  ExportComplianceTrendsReport,
  ExportComplianceTrendsReportSuccess,
  GetComplianceTrendsBreakdown,
  GetComplianceTrendsBreakdownSuccess,
  GetComplianceTrendsBreakdownError,
  ExportComplianceTrendsBreakdown,
  ExportComplianceTrendsBreakdownSuccess,
  ExportComplianceTrendsBreakdownError,
  ResetComplianceTrendsBreakdown,
} from "@modules/reporting/constants/actions";
import type { ComplianceReportViewState, ComplianceState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: ComplianceReportViewState = {
  filtersExpanded: false,
  chartsExpanded: true,
  reportTableExpanded: true,
  activeFilter: "ALL",
  showFractions: false,
  showTotals: false,
  showBreakdownModal: false,
};

export const initialState: ComplianceState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  defaultReports: [],
  myReports: [],
  report: {
    details: null,
    reportData: {
      details: [],
      headlines: null,
    },
    breakdownData: {
      filters: null,
      details: {
        name: null,
        fromDate: null,
        toDate: null,
        completed: null,
        expired: null,
        total: null,
        compliant: null,
      },
      list: [],
    },
  },
  schema: null,
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: ComplianceState) => () => startRequest(state);

export const onError = (state: ComplianceState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const setLoaded = (state: ComplianceState) => () => {
  return {
    ...finishRequest(state),
  };
};

export const onGetComplianceTrendsDefaultReportsSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetComplianceTrendsMyReportsSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    myReports: payload,
  };
};

export const onGetComplianceTrendsReportDataSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    report: {
      ...state.report,
      ...payload,
    },
  };
};

export const onGetComplianceTrendsReportSchemaSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    schema: payload,
  };
};

export const onGetComplianceTrendsReportSchemaError = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    error: payload,
  };
};

export const onSetComplianceTrendsReportToSave = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onReSetComplianceTrendsReportToSave = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: null,
  };
};

export const onSetViewState = (state: ComplianceState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: ComplianceState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportComplianceTrendsReport = (state: ComplianceState) => () => ({
  ...state,
  reportGenerateInProgress: ++state.reportGenerateInProgress,
});

export const onExportComplianceTrendsReportSuccess = (state: ComplianceState) => () => ({
  ...state,
  reportGenerateInProgress:
    state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
});

export const onGetComplianceTrendsBreakdownSuccess = (state: ComplianceState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    report: {
      ...state.report,
      breakdownData: payload,
    },
    viewState: {
      ...state.viewState,
      showBreakdownModal: true,
    },
  };
};

export const onResetComplianceTrendsBreakdown = (state: ComplianceState) => () => {
  return {
    ...state,
    report: {
      ...state.report,
      breakdownData: {
        filters: null,
        details: {
          name: null,
          fromDate: null,
          toDate: null,
          completed: null,
          expired: null,
          total: null,
          compliant: null,
        },
        list: [],
      },
    },
    viewState: {
      ...state.viewState,
      showBreakdownModal: false,
    },
  };
};

export default (state: ComplianceState = initialState, action: Object): ComplianceState => {
  const act = {
    [ExportComplianceTrendsReport]: onExportComplianceTrendsReport,
    [ExportComplianceTrendsReportSuccess]: onExportComplianceTrendsReportSuccess,
    [GetComplianceTrendsDefaultReports]: setLoading,
    [GetComplianceTrendsDefaultReportsSuccess]: onGetComplianceTrendsDefaultReportsSuccess,
    [GetComplianceTrendsDefaultReportsError]: onError,
    [GetComplianceTrendsMyReports]: setLoading,
    [GetComplianceTrendsMyReportsSuccess]: onGetComplianceTrendsMyReportsSuccess,
    [GetComplianceTrendsMyReportsError]: onError,
    [GetComplianceTrendsReportData]: setLoading,
    [GetComplianceTrendsReportDataSuccess]: onGetComplianceTrendsReportDataSuccess,
    [GetComplianceTrendsReportDataError]: onError,
    [GetComplianceTrendsReportSchemaSuccess]: onGetComplianceTrendsReportSchemaSuccess,
    [GetComplianceTrendsReportSchemaError]: onGetComplianceTrendsReportSchemaError,
    [SetComplianceTrendsReportToSave]: onSetComplianceTrendsReportToSave,
    [ReSetComplianceTrendsReportToSave]: onReSetComplianceTrendsReportToSave,
    [SetComplianceReportViewState]: onSetViewState,
    [GetComplianceTrendsBreakdown]: setLoading,
    [GetComplianceTrendsBreakdownSuccess]: onGetComplianceTrendsBreakdownSuccess,
    [GetComplianceTrendsBreakdownError]: onError,
    [ResetComplianceTrendsBreakdown]: onResetComplianceTrendsBreakdown,
    [ExportComplianceTrendsBreakdown]: setLoading,
    [ExportComplianceTrendsBreakdownSuccess]: setLoaded,
    [ExportComplianceTrendsBreakdownError]: onError,
    [LOCATION_CHANGE]: onResetViewState,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
